import React, { useCallback, useEffect, useState } from 'react'
import { Dropdown, Offcanvas } from 'react-bootstrap'
import { useFieldArray, useForm, UseFormRegister, UseFormWatch } from 'react-hook-form'
import { privateApi } from '../../../api/api'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import FormSpinner from '../../../shared/components/spinner/FormSpinner'
import { IconExclamationCircle, IconPlus, IconGripVertical } from '@tabler/icons-react'
import { extractErrorMessage } from '../../../shared/utils/serverValidation'
import CloseRoomConfirmationModal from './CloseRoomConfirmationModal'
import DateInput from '../../../shared/components/form/DateInput'
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent
} from '@dnd-kit/core'
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useSortRoomNumbers } from '../hooks/useSortRoomNumbers'

// Local calendar date (yyyy-MM-dd). `toISOString()` is UTC-based and would return
// the previous/next day for users in non-UTC timezones (e.g. WITA/UTC+8 before 08:00).
const toLocalDateString = (d: Date) => {
    const month = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    return `${d.getFullYear()}-${month}-${day}`;
};

const today = () => toLocalDateString(new Date());

// Backend (RoomNumberController::store) izinkan start_date mundur maksimal 30 hari
// dari hari ini -- dipakai untuk kasus room number yang telat didaftarkan padahal
// sudah dipakai booking direct beberapa hari sebelumnya. Batas `min` di sini hanya
// untuk UX (mencegah user memilih tanggal yang pasti ditolak 422); backend tetap
// jadi sumber kebenaran validasinya.
const minStartDate = () => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return toLocalDateString(d);
};

// Room number baru/status berubah mengubah total_rooms per tanggal, jadi grid
// availability (Monthly Setup, Calendar) & occupancy yang sedang di-cache React
// Query WAJIB di-invalidate juga -- kalau tidak, angkanya kelihatan "tidak berubah"
// sampai user reload manual (refetchOnWindowFocus dimatikan di hook-hook itu).
const invalidateAvailabilityQueries = (queryClient: ReturnType<typeof useQueryClient>) => {
    return queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.some((key) =>
            typeof key === 'string' && ['availability', 'occupancy', 'occupancy-daily'].includes(key)
        ),
    });
};

interface MovedBooking {
    booking_room_id: string,
    invoice_number: string,
    from_room_number: string,
    to_room_number: string | null,
    unassigned: boolean,
}

interface UpdateRoomNumberDialogProps {
    show: boolean,
    onClose: () => void,
    roomNumberId: string
}

interface UpdateRoomNumberForm {
    room_number: {
        id: string,
        room_id: string,
        room_number: string,
        status: string,
        condition: string,
        isNew?: boolean,
        room_type_id: string,
        start_date?: string,
        sort_order?: number,
    }[]
}

const UpdateRoomNumberDialog: React.FC<UpdateRoomNumberDialogProps> = ({ show, onClose, roomNumberId }) => {
    const { data: roomType, isFetching: roomTypeIsFetching, isError: roomTypeIsError } = useQuery({
        queryKey: ['rooms', 'types', roomNumberId],
        queryFn: () => privateApi.get(`/v4/room-type/${roomNumberId}`).then((res) => res.data),
        enabled: !!roomNumberId,
    });

    const { data: roomNumber, isFetching: roomNumberIsFetching, isError: roomNumberIsError } = useQuery({
        queryKey: ['rooms', 'numbers', roomNumberId],
        queryFn: () => privateApi.get(`/v3/room-number`, {
            params: {
                filter: {
                    room_type_id: roomNumberId
                },
                // Tidak ada orderBy/sort di sini secara sengaja -- backend sekarang
                // mengembalikan rooms terurut default berdasarkan sort_order (diatur
                // lewat drag-and-drop). Override manual di sini akan membuat dialog
                // ini menampilkan urutan berbeda dari kalender.
                all: 1,
            }
        }).then((res) => res.data),
        enabled: !!roomNumberId,
    });

    if (roomNumberIsError || roomTypeIsError) {
        toast.error(roomNumber.error?.response?.data?.message || 'An error occurred', { id: 'update-room-number' });
    }

    const { handleSubmit, register, reset, control, watch } = useForm<UpdateRoomNumberForm>({
        defaultValues: {
            room_number: [],
        }
    });

    const { fields, append, remove, move } = useFieldArray({
        control,
        name: 'room_number',
        keyName: 'id',
    });

    const sortRoomNumbers = useSortRoomNumbers();

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = fields.findIndex((f) => f.id === active.id);
        const newIndex = fields.findIndex((f) => f.id === over.id);
        if (oldIndex < 0 || newIndex < 0) return;

        // Jangan pernah kirim daftar yang belum lengkap ke server (baris baru belum
        // punya room_id, server akan menolak dengan 409). Cek ini HARUS dilakukan
        // SEBELUM move(), supaya UI tidak menampilkan urutan baru yang sebenarnya
        // tidak pernah tersimpan dan diam-diam balik lagi saat dialog dibuka ulang.
        if (fields.some((f) => f.isNew)) {
            toast.warning('Save or cancel the new room number first before reordering.', {
                id: 'sort-room-number-blocked',
            });
            return;
        }

        move(oldIndex, newIndex);
        const next = arrayMove(fields, oldIndex, newIndex);
        const roomIds = next.map((f) => f.room_id);
        sortRoomNumbers.mutate({ roomTypeId: roomNumberId, roomIds });
    };

    const handleAdd = () => {
        append({
            id: '',
            room_id: '',
            room_number: '',
            status: 'available',
            condition: 'clean',
            isNew: true,
            room_type_id: roomNumberId,
            start_date: today(),
        });
    };


    const handleRemoveRoom = (index: number) => {
        remove(index);
    };


    useEffect(() => {
        if (roomNumber?.data) {
            const updatedRoomNumber = roomNumber.data.map((room: any) => ({
                ...room,
                room_id: room.id,
                isNew: false,
            }));

            reset({
                room_number: updatedRoomNumber,
            });
        }
    }, [roomNumber, reset]);

    const handleClose = () => {
        reset();
        onClose();
    }

    const onSubmit = useCallback(() => {
        return;
    }, []);

    return (
        <Offcanvas show={show} onHide={handleClose} placement="end" className="offcanvas-responsive">
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>Update Room Number</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                {roomNumberIsFetching || roomTypeIsFetching && <FormSpinner />}
                {roomNumberIsError || roomTypeIsError && (
                    <div className='d-flex justify-content-center align-items-center' style={{ height: '100%' }}>
                        <div className='text-center text-danger'>
                            <IconExclamationCircle size={72} />
                            <p className='mt-3'>An error occurred</p>
                        </div>
                    </div>
                )}
                {roomNumber?.success && roomType?.success && (
                    <form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column" style={{ height: '100%' }}>
                        <div className="action-sheet-content flex-grow-1">
                            <div className='row mb-3 align-items-center'>
                                <label htmlFor='room_type' className='col-sm-4 col-form-label d-flex justify-content-md-end' style={{ fontSize: '14px' }}>Room Type: </label>
                                <div className='col-sm-8'>
                                    <span className='badge bg-primary text-white'>{roomType?.data?.name}</span>
                                </div>
                            </div>
                            <div style={sortRoomNumbers.isPending ? { opacity: 0.6, pointerEvents: 'none' } : undefined}>
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleDragEnd}
                                >
                                    <SortableContext
                                        items={fields.map((f) => f.id)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        {fields.map((field, index) => (
                                            <SortableRoomNumberRow
                                                key={field.id}
                                                field={field}
                                                register={register}
                                                watch={watch}
                                                index={index}
                                                remove={handleRemoveRoom}
                                            />
                                        ))}
                                    </SortableContext>
                                </DndContext>
                            </div>
                            <div className='row mb-3 align-items-center'>
                                <div className='col-sm-10 offset-sm-2 border-top d-flex pt-3 justify-content-center'>
                                    <button type='button' className='p-0 border-0 bg-white d-flex align-items-center' onClick={() => handleAdd()}>
                                        <div className='border-1 rounded-circle bg-primary'>
                                            <IconPlus size={24} className='text-white' />
                                        </div>
                                        <span className='ms-2'>Add Room Number</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                )}
            </Offcanvas.Body>
        </Offcanvas>
    )
}

export default UpdateRoomNumberDialog

interface RoomNumberFieldProps {
    field: {
        id: string,
        room_id: string,
        room_number: string,
        status: string,
        condition: string,
        isNew?: boolean,
        room_type_id: string,
        start_date?: string,
        sort_order?: number,
    },
    register: UseFormRegister<UpdateRoomNumberForm>,
    watch: UseFormWatch<UpdateRoomNumberForm>,
    index: number,
    remove: (index: number) => void
}

// Membungkus RoomNumberField dengan drag handle untuk drag-and-drop sort urutan
// room number. Handle drag hanya dipasang di icon grip, BUKAN di seluruh baris,
// supaya klik/ketik di input room number tidak memicu drag.
const SortableRoomNumberRow: React.FC<RoomNumberFieldProps> = (props) => {
    const { field } = props;
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
        id: field.id,
        disabled: !!field.isNew, // baris baru yang belum disimpan belum punya room_id asli, drag tidak berguna
    });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div ref={setNodeRef} style={style} className="d-flex align-items-center">
            <span
                {...(field.isNew ? {} : attributes)}
                {...(field.isNew ? {} : listeners)}
                className="me-2"
                style={{ cursor: field.isNew ? 'not-allowed' : 'grab', opacity: field.isNew ? 0.3 : 1 }}
            >
                <IconGripVertical size={18} className="text-muted" />
            </span>
            <div className="flex-grow-1">
                <RoomNumberField {...props} />
            </div>
        </div>
    );
};

interface UpdateRoomNumberValues {
    status: string,
    room_number: string,
    reassign_bookings?: boolean,
}

interface AddRoomNumberValues {
    room_type_id: string,
    room_number: Array<string>,
    start_date?: string,
}

const RoomNumberField: React.FC<RoomNumberFieldProps> = ({ field, register, watch, index, remove }) => {
    const queryClient = useQueryClient();
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);

    const { mutate, isPending } = useMutation({
        mutationFn: (data: UpdateRoomNumberValues) => privateApi.put(`/v3/room-number/${field.room_id}`, data),
        onSuccess: async (res) => {
            await Promise.all([
                queryClient.refetchQueries({
                    predicate: (query) => query.queryKey.some((key) => ['rooms', 'numbers'].includes(key as string))
                }),
                invalidateAvailabilityQueries(queryClient),
            ]);

            toast.success(`Room number ${field.room_number} updated successfully`, { id: `update-room-number-${index}` });

            const movedBookings: MovedBooking[] = res?.data?.data?.moved_bookings ?? [];
            if (movedBookings.length > 0) {
                const movedCount = movedBookings.filter((b) => !b.unassigned).length;
                const unassignedCount = movedBookings.filter((b) => b.unassigned).length;
                const parts = [];
                if (movedCount > 0) parts.push(`${movedCount} reservation(s) moved to another room`);
                if (unassignedCount > 0) parts.push(`${unassignedCount} reservation(s) unassigned (no room available)`);
                toast.success(parts.join(', '), { id: `moved-bookings-${index}`, duration: 8000 });
            }
        },
        onError: (error) => {
            toast.error(extractErrorMessage(error) || 'An error occurred', { id: `update-room-number-${index}` });
        },
    });

    const handleChange = useCallback((data: UpdateRoomNumberValues) => {
        toast.loading(`Updating room number ${field.room_number}`, { id: `update-room-number-${index}` });
        mutate(data);
    }, [mutate]);

    const handleConfirmClose = useCallback((reassignBookings: boolean) => {
        setShowCloseConfirm(false);
        handleChange({
            status: 'closed',
            room_number: field.room_number,
            reassign_bookings: reassignBookings,
        });
    }, [handleChange, field.room_number]);

    const currentRoomNumber = watch(`room_number.${index}.room_number`);
    const initialRoomNumber = field.room_number;

    const isChanged = currentRoomNumber !== initialRoomNumber;

    const { mutate: mutateNewRoomNumber, isPending: isPendingForNewRoomNumber } = useMutation({
        mutationFn: (data: AddRoomNumberValues) => privateApi.post(`/v3/room-number`, data),
        onSuccess: async () => {
            await Promise.all([
                queryClient.refetchQueries({
                    predicate: (query) => query.queryKey.some((key) => ['rooms', 'numbers'].includes(key as string))
                }),
                invalidateAvailabilityQueries(queryClient),
            ]);

            toast.success(`Room number ${currentRoomNumber} added successfully`, { id: `add-room-number-${index}` });
        },
        onError: (error) => {
            toast.error(extractErrorMessage(error) || 'An error occurred', { id: `add-room-number-${index}` });
        },
    });

    const handleNewRoomNumber = useCallback((values: AddRoomNumberValues) => {
        toast.loading(`Adding room number ${currentRoomNumber}`, { id: `add-room-number-${index}` });
        mutateNewRoomNumber(values);
    }, [mutateNewRoomNumber]);

    return (
        <div className="row mb-3 align-items-center" >
            <label htmlFor="room_number" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Room Number: </label>
            <div className="col-sm-8 d-flex">
                <input
                    type="text"
                    className="form-control app-form-control rounded-1"
                    id="room_number"
                    placeholder="Room Number"
                    {...register(`room_number.${index}.room_number` as const)}
                    style={{ fontSize: '14px', height: '35px' }}
                />
                {field.isNew && (
                    <DateInput
                        className="form-control app-form-control rounded-1 ms-2"
                        id="start_date"
                        title="Available from (up to 30 days in the past for backdated local availability; the Channel Manager sync always starts today)"
                        min={minStartDate()}
                        {...register(`room_number.${index}.start_date` as const)}
                        style={{ fontSize: '14px', height: '35px', maxWidth: '160px' }}
                    />
                )}
                <div className='d-flex'>
                    {!field.isNew && (
                        <Dropdown>
                            <Dropdown.Toggle variant={field.status === 'available' ? 'outline-success' : 'outline-danger'} size='sm' id="action-dropdown" className='h-100 ms-2'>
                                {field.status}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item
                                    onClick={() => {
                                        handleChange({
                                            status: 'available',
                                            room_number: field.room_number,
                                        });
                                    }}
                                    disabled={field.status === 'available' || isPending}
                                >Available</Dropdown.Item>
                                <Dropdown.Item
                                    onClick={() => setShowCloseConfirm(true)}
                                    disabled={field.status === 'closed' || isPending}
                                >Closed</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    )}
                    <CloseRoomConfirmationModal
                        show={showCloseConfirm}
                        roomNumber={field.room_number}
                        isSubmitting={isPending}
                        onClose={() => setShowCloseConfirm(false)}
                        onConfirm={handleConfirmClose}
                    />
                    {isChanged && !field.isNew && (
                        <button
                            type='button'
                            className='btn btn-sm btn-outline-primary ms-2'
                            onClick={() => handleChange({
                                status: field.status,
                                room_number: watch(`room_number.${index}.room_number`),
                            })}
                            disabled={isPending}
                        >
                            Save
                        </button>
                    )}
                    {field.isNew && (
                        <>
                            {/* handleRemoveRoom */}
                            <button
                                type='button'
                                className='btn btn-sm btn-outline-danger ms-2'
                                onClick={() => remove(index)}
                            >
                                Cancel
                            </button>
                            <button
                                type='button'
                                className='btn btn-sm btn-outline-primary ms-2'
                                onClick={() => handleNewRoomNumber({
                                    room_type_id: field.room_type_id,
                                    room_number: [watch(`room_number.${index}.room_number`)],
                                    start_date: watch(`room_number.${index}.start_date`) || undefined,
                                })}
                                disabled={isPendingForNewRoomNumber}
                            >
                                Save
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div >
    )
}